
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  CheckSquare, 
  Plus, 
  Clock, 
  AlertCircle, 
  User,
  Calendar,
  Filter,
  Search
} from 'lucide-react';

const HQTasks = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const tasks = [
    {
      id: 1,
      title: "Complete Houston staff training audit",
      description: "Review training completion status for all Houston team members",
      priority: "high",
      status: "in-progress",
      assignee: "Sarah Johnson",
      dueDate: "2024-02-15",
      category: "training"
    },
    {
      id: 2,
      title: "Update Botox injection SOPs",
      description: "Incorporate new safety guidelines from recent training",
      priority: "medium",
      status: "pending",
      assignee: "Michael Chen",
      dueDate: "2024-02-20",
      category: "sops"
    },
    {
      id: 3,
      title: "Launch Austin marketing campaign",
      description: "Deploy Q1 marketing materials and social media content",
      priority: "high",
      status: "completed",
      assignee: "Emily Rodriguez", 
      dueDate: "2024-02-10",
      category: "marketing"
    },
    {
      id: 4,
      title: "Equipment maintenance scheduling",
      description: "Schedule quarterly maintenance for all clinic equipment",
      priority: "low",
      status: "pending",
      assignee: "David Park",
      dueDate: "2024-02-25",
      category: "operations"
    }
  ];

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-500/20 text-red-200 border-red-500/30';
      case 'medium': return 'bg-amber-500/20 text-amber-200 border-amber-500/30';
      case 'low': return 'bg-green-500/20 text-green-200 border-green-500/30';
      default: return 'bg-slate-500/20 text-slate-200 border-slate-500/30';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-emerald-500/20 text-emerald-200 border-emerald-500/30';
      case 'in-progress': return 'bg-blue-500/20 text-blue-200 border-blue-500/30';
      case 'pending': return 'bg-slate-500/20 text-slate-200 border-slate-500/30';
      default: return 'bg-slate-500/20 text-slate-200 border-slate-500/30';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-amber-900">
      {/* Header */}
      <div className="bg-slate-800/50 backdrop-blur border-b border-amber-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-white">
                Task Management
              </h1>
              <p className="mt-1 text-amber-200">
                HQ-level task manager and rollout action items
              </p>
            </div>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white">
              <Plus className="w-4 h-4 mr-2" />
              Create Task
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Search and Filters */}
          <Card className="bg-slate-800/50 backdrop-blur border-slate-600/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Search tasks..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400"
                  />
                </div>
                <div className="flex space-x-3">
                  <Button variant="outline" className="border-slate-600 text-slate-300">
                    <Filter className="w-4 h-4 mr-2" />
                    Priority
                  </Button>
                  <Button variant="outline" className="border-slate-600 text-slate-300">
                    <Filter className="w-4 h-4 mr-2" />
                    Status
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Task Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card className="bg-slate-800/50 backdrop-blur border-emerald-500/20">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-emerald-500/20">
                    <CheckSquare className="h-6 w-6 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-sm text-emerald-200">Completed</p>
                    <p className="text-2xl font-bold text-white">24</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 backdrop-blur border-blue-500/20">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-blue-500/20">
                    <Clock className="h-6 w-6 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm text-blue-200">In Progress</p>
                    <p className="text-2xl font-bold text-white">8</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 backdrop-blur border-amber-500/20">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-amber-500/20">
                    <AlertCircle className="h-6 w-6 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm text-amber-200">Pending</p>
                    <p className="text-2xl font-bold text-white">12</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 backdrop-blur border-red-500/20">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-red-500/20">
                    <Calendar className="h-6 w-6 text-red-400" />
                  </div>
                  <div>
                    <p className="text-sm text-red-200">Overdue</p>
                    <p className="text-2xl font-bold text-white">3</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Task List */}
          <Card className="bg-slate-800/50 backdrop-blur border-slate-600/30">
            <CardHeader>
              <CardTitle className="text-white">Active Tasks</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {tasks.map((task) => (
                  <div key={task.id} className="p-4 bg-slate-700/30 rounded-lg border border-slate-600/30">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-white font-semibold">{task.title}</h3>
                          <Badge className={getPriorityColor(task.priority)}>
                            {task.priority}
                          </Badge>
                          <Badge className={getStatusColor(task.status)}>
                            {task.status}
                          </Badge>
                        </div>
                        <p className="text-slate-300 text-sm mb-3">{task.description}</p>
                        <div className="flex items-center space-x-4 text-sm text-slate-400">
                          <div className="flex items-center space-x-1">
                            <User className="w-4 h-4" />
                            <span>{task.assignee}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Calendar className="w-4 h-4" />
                            <span>Due {task.dueDate}</span>
                          </div>
                          <Badge className="bg-purple-500/20 text-purple-200 border-purple-500/30 text-xs">
                            {task.category}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Button size="sm" variant="outline" className="border-slate-600 text-slate-300">
                          Edit
                        </Button>
                        <Button size="sm" className="bg-amber-600 hover:bg-amber-700">
                          Update
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default HQTasks;
